import { useCallback, useMemo, useState } from "react";

import { useValidateCustomFormLazyQuery } from "@/apollo/types";
import { Alert, AlertIcon, AlertTitle } from "@/components/elements/Alert";
import { Button, IconButton } from "@/components/elements/Button";
import Select from "@/components/elements/Select";
import { InputError } from "@/components/primitives/InputError";
import FieldLabel from "@/components/primitives/InputLabel";
import { Input } from "@/components/primitives/input";
import { useMountEffect } from "@/hooks/useMountEffect";
import { DropdownOption } from "@/integrations";
import { XIcon } from "lucide-react";

import {
  GorgiasReport,
  GorgiasScopeGroup,
  isReportLocallyValid,
} from "./GorgiasForm";

export default function GorgiasCustomReport(props: {
  onChangeReport: (report: GorgiasReport) => void;
  onRemoveReport: (report: GorgiasReport) => void;
  onReportInvalid: (reportId: string) => void;
  onReportValid: (reportId: string) => void;
  report: GorgiasReport;
  connectionId: string;
  scopeGroups: GorgiasScopeGroup[];
  validationError?: string;
}) {
  const {
    report,
    onChangeReport,
    onRemoveReport,
    validationError,
    connectionId,
    scopeGroups,
    onReportValid,
    onReportInvalid,
  } = props;

  const scopeOptions = scopeGroups.map((g) => g.scope);

  // Per-report DOM id prefix so labels/inputs are correctly associated and
  // ids don't collide when multiple custom reports are rendered.
  const idPrefix = `gorgias-report-${report.id}`;
  const fieldId = {
    name: `${idPrefix}-name`,
    scope: `${idPrefix}-scope`,
    measures: `${idPrefix}-measures`,
    measuresSelectAll: `${idPrefix}-measures-select-all`,
    dimensions: `${idPrefix}-dimensions`,
    timeDimension: `${idPrefix}-time-dimension`,
    granularity: `${idPrefix}-granularity`,
  };

  const [validationSuccess, setValidationSuccess] = useState<boolean>(false);
  const [hasUserValidated, setHasUserValidated] = useState<boolean>(false);
  const [validationCallError, setValidationCallError] = useState<string | null>(
    null,
  );

  type TouchableField =
    | "name"
    | "scope"
    | "measures"
    | "dimensions"
    | "timeDimension"
    | "granularity";
  const [touched, setTouched] = useState<Record<TouchableField, boolean>>({
    name: false,
    scope: false,
    measures: false,
    dimensions: false,
    timeDimension: false,
    granularity: false,
  });
  const markTouched = useCallback((field: TouchableField) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  const [callValidationQuery] = useValidateCustomFormLazyQuery();

  const selectedScopeGroup = useMemo(
    () =>
      scopeGroups.find((g) => g.scope.value === report.scope?.value) ?? null,
    [scopeGroups, report.scope?.value],
  );

  const measures = selectedScopeGroup?.measures ?? [];
  const dimensions = selectedScopeGroup?.dimensions ?? [];
  const timeDimensions = selectedScopeGroup?.timeDimensions ?? [];
  const granularities = selectedScopeGroup?.granularities ?? [];

  const invalidateReport = useCallback(
    (message?: string) => {
      // When called without a message we are clearing previous server-side
      // errors as well — otherwise a stale `validationCallError` remains
      // visible after the user edits the report.
      setValidationCallError(message ?? null);
      setValidationSuccess(false);
      onReportInvalid(report.id);
    },
    [onReportInvalid, report.id],
  );

  const isLocallyValid = useCallback(
    (r: GorgiasReport): string | null => isReportLocallyValid(r),
    [],
  );

  const runValidation = useCallback(async () => {
    setTouched({
      name: true,
      scope: true,
      measures: true,
      dimensions: true,
      timeDimension: true,
      granularity: true,
    });
    const localError = isLocallyValid(report);
    if (localError) {
      invalidateReport(localError);
      return;
    }

    const { data, error } = await callValidationQuery({
      variables: {
        input: {
          connectionId,
          payload: JSON.stringify({
            report,
          }),
        },
      },
      fetchPolicy: "network-only",
    });
    if (error) {
      invalidateReport(error.message);
      return;
    }

    const result = data?.validateCustomForm.customFormValidationResult;
    if (!result) {
      invalidateReport("Validation failed");
      return;
    }
    if (result.valid) {
      setValidationSuccess(true);
      onReportValid(report.id);
      setValidationCallError(null);
    } else {
      invalidateReport(result.errorMessage);
    }
  }, [
    isLocallyValid,
    report,
    callValidationQuery,
    connectionId,
    invalidateReport,
    onReportValid,
  ]);

  const changeReport = (next: GorgiasReport) => {
    onChangeReport(next);
    setValidationSuccess(false);
    invalidateReport();
  };

  const changeReportScope = (next: GorgiasReport) => {
    // Reset scope-dependent fields
    next.measures = [];
    next.dimensions = [];
    next.timeDimension = undefined;
    next.granularity = undefined;
    onChangeReport(next);
    setValidationSuccess(false);
    invalidateReport();
  };

  useMountEffect(() => {
    if (report.scope) {
      runValidation();
    }
  });

  return (
    <div className="relative rounded-xs border bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700">
      <div className="pb-4">
        <FieldLabel htmlFor={fieldId.name} required={true}>
          Name
        </FieldLabel>
        <Input
          id={fieldId.name}
          placeholder="Name of your report"
          value={report.name}
          onChange={(e) => {
            markTouched("name");
            changeReport({ ...report, name: e.target.value });
          }}
        />
        {touched.name && !report.name && (
          <InputError>Report name is required</InputError>
        )}
      </div>
      <div className="pb-4">
        <FieldLabel htmlFor={fieldId.scope} required={true}>
          Scope
        </FieldLabel>
        <Select
          inputId={fieldId.scope}
          placeholder="Select a scope..."
          onChange={(v: DropdownOption<string>) => {
            markTouched("scope");
            changeReportScope({
              ...report,
              scope: v,
            });
          }}
          options={scopeOptions}
          value={report.scope}
          hideSelectedOptions={false}
          isMulti={false}
        />
        {touched.scope && !report.scope?.value && (
          <InputError>Report scope is required</InputError>
        )}
      </div>
      {selectedScopeGroup && (
        <>
          <div className="pb-4">
            <div className="flex h-auto justify-between">
              <FieldLabel htmlFor={fieldId.measures} required={true}>
                Measures
              </FieldLabel>
              <Button
                onClick={() => {
                  markTouched("measures");
                  changeReport({
                    ...report,
                    measures: measures,
                  });
                }}
                size="sm"
                variant="link"
                id={fieldId.measuresSelectAll}
              >
                Select all
              </Button>
            </div>
            <Select
              inputId={fieldId.measures}
              placeholder="Select one or more..."
              onChange={(v: DropdownOption<string>[]) => {
                markTouched("measures");
                changeReport({
                  ...report,
                  measures: v,
                });
                if (!v.length) {
                  invalidateReport("At least one measure is required");
                }
              }}
              options={measures}
              value={report.measures}
              hideSelectedOptions={true}
              isMulti={true}
              closeMenuOnSelect={false}
            />
            {touched.measures && !(report.measures ?? []).length && (
              <InputError>At least one measure is required</InputError>
            )}
          </div>

          {dimensions.length > 0 && (
            <div className="pb-4">
              <FieldLabel htmlFor={fieldId.dimensions}>Dimensions</FieldLabel>
              <Select
                inputId={fieldId.dimensions}
                placeholder="Select one or more..."
                onChange={(v: DropdownOption[]) => {
                  markTouched("dimensions");
                  changeReport({
                    ...report,
                    dimensions: v,
                  });
                }}
                options={dimensions}
                value={report.dimensions}
                hideSelectedOptions={true}
                isMulti={true}
                closeMenuOnSelect={false}
              />
            </div>
          )}

          <div className="pb-4">
            <FieldLabel htmlFor={fieldId.timeDimension} required>
              Time dimension
            </FieldLabel>
            <Select
              inputId={fieldId.timeDimension}
              placeholder="Select one..."
              onChange={(v: DropdownOption) => {
                if (!v) return;
                markTouched("timeDimension");
                // Preserve a previously chosen granularity only if it is
                // still valid for the current scope; otherwise fall back to
                // 'day' (if present) or the first available option so the
                // field is never left empty or stale.
                const preservedGranularity =
                  report.granularity &&
                  granularities.some(
                    (g) => g.value === report.granularity?.value,
                  )
                    ? report.granularity
                    : undefined;
                changeReport({
                  ...report,
                  timeDimension: v,
                  granularity:
                    preservedGranularity ??
                    granularities.find((g) => g.value === "day") ??
                    granularities[0],
                });
              }}
              options={timeDimensions}
              value={report.timeDimension ?? null}
              hideSelectedOptions={false}
              isMulti={false}
              isClearable={false}
            />
            {touched.timeDimension && !report.timeDimension?.value && (
              <InputError>Time dimension is required</InputError>
            )}
          </div>

          {report.timeDimension && (
            <div className="pb-4">
              <FieldLabel htmlFor={fieldId.granularity} required>
                Granularity
              </FieldLabel>
              <Select
                inputId={fieldId.granularity}
                placeholder="Select one..."
                onChange={(v: DropdownOption<string>) => {
                  if (!v) return;
                  markTouched("granularity");
                  changeReport({
                    ...report,
                    granularity: v,
                  });
                }}
                options={granularities}
                value={report.granularity ?? null}
                hideSelectedOptions={false}
                isMulti={false}
                isClearable={false}
              />
            </div>
          )}
        </>
      )}
      <div className="flex justify-between">
        <Button
          size="sm"
          variant="solid"
          colorScheme="primary"
          onClick={() => {
            setHasUserValidated(true);
            runValidation();
          }}
        >
          Validate report
        </Button>
      </div>
      <div className="absolute top-0 right-0 p-2">
        <IconButton
          size="xs"
          icon={<XIcon />}
          variant="ghost"
          onClick={() => onRemoveReport(report)}
        />
      </div>
      {validationSuccess && hasUserValidated && !validationError ? (
        <div className="mt-2">
          <Alert status="success">
            <AlertIcon />
            Report is valid
          </Alert>
        </div>
      ) : null}
      {validationError ? (
        <div className="mt-2">
          <Alert status="error">
            <AlertIcon />
            {validationError}
          </Alert>
        </div>
      ) : null}
      {!validationCallError && !validationSuccess && !validationError ? (
        <div className="mt-2">
          <Alert status="info">
            <AlertIcon />
            Validate your report to continue
          </Alert>
        </div>
      ) : null}
      {validationCallError ? (
        <div className="mt-2">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error:</AlertTitle>
            {validationCallError}
          </Alert>
        </div>
      ) : null}
    </div>
  );
}
